import { Box } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import SystemDialog from "../../../../../../components/UIToolkit/Popups/Dialog";
import { NurseRole } from "../../../../../../utils/models";

export interface RolePickerDialogProps {
	open: boolean;
	onConfirm: (role: NurseRole) => void;
	onClose: () => void;
}

/**
 * Collects the in-house role a role string syncs as — the one field the backend requires that the
 * row cannot supply itself (a DontSync row has had its role cleared; an orphan never had one).
 * Opened by `JobPickerCell` only for those rows; anything already carrying a role reuses it.
 *
 * Nothing is pre-selected. The role is written onto every nurse the role string syncs, so a
 * one-click Confirm must not be able to stamp a guess — Confirm stays disabled until one is picked.
 */
export const RolePickerDialog: FC<RolePickerDialogProps> = ({ open, onConfirm, onClose }) => {
	const { t } = useTranslation();
	const [role, setRole] = useState<NurseRole | undefined>(undefined);

	const roleItems = useMemo(() => Object.values(NurseRole).map((r) => ({ value: r, label: r, selectedLabel: r })), []);

	// No state reset on confirm/close: `JobPickerCell` mounts this dialog conditionally, so it
	// unmounts — and drops its state — the moment either handler runs.
	const handleConfirm = () => {
		if (!role) return;
		onConfirm(role);
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hris.mapping.rolePicker.title")}
			dataTestId="hris-mapping-role-picker-dialog"
			confirmDisabled={!role}
			onConfirm={handleConfirm}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, minWidth: 320 }}>
					<SystemDropdown<NurseRole>
						label={t("hris.mapping.rolePicker.roleLabel")}
						items={roleItems}
						selectedItem={role}
						onChange={setRole}
						fullWidth
						data-testid="hris-mapping-role-picker-role"
					/>
				</Box>
			}
		/>
	);
};
